import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useBreakpoints from 'hooks/use-breakpoints';
import ModalContext from '../../context/modal';
import AppointmentBooking from 'components/appointment-booking-modal/appointment-booking';
import Modal from 'components/modal';
import Collapse from 'react-tiny-collapse';
import ContentContainer from 'components/content-container';
import Form from 'components/form/form';
import TextInput from 'components/text-input/text-input';
import AppointmentBookingAgreeBox from 'components/appointment-booking-modal/appointment-booking-agree-box';
import AppointmentBookingFooter from 'components/appointment-booking-modal/appointment-booking-footer';

const TIME_BOOKING_PARAM_NAME = 'bestill-time';
const SHOP_ID_PARAM_NAME = 'shopId';
const APPOINTMENT_TYPE_PARAM_NAME = 'appType';
const OPTICIAN_ID_PARAM_NAME = 'optician';

const AppointmentBookingModal = props => {
  const { isTablet } = useBreakpoints(true, true);
  const ModalContentWrapper = isTablet ? 'div' : ContentContainer;
  const modalContext = useContext(ModalContext);
  const appBookId = 'appointment-booking-modal';

  const hasOpenedOnLoad = useRef(false);

  const [preselectedValues, setPreselectedValues] = useState({
    shopId: props.preselectedShopId,
    appointmentId: props.preselectedAppointmentId,
    opticianId: props.preselectedOpticianId
  });

  // Automatically open the AppointmentBookingModal on page load based on conditions below
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    let shouldOpenFromQueryString = false;

    // If we get props.checkQueryString = trur from BE we understand we need to check query string and based on this show modal with
    // the params from query string
    if (props.checkQueryString) {
      // if TIME_BOOKING_PARAM_NAME is present in query string, modal should be opened
      shouldOpenFromQueryString = urlParams.has(TIME_BOOKING_PARAM_NAME);

      // Collect preselected values for booking from query string
      setPreselectedValues({
        shopId: urlParams.get(SHOP_ID_PARAM_NAME) || props.preselectedShopId,
        appointmentId: urlParams.get(APPOINTMENT_TYPE_PARAM_NAME)
          ? urlParams.get(APPOINTMENT_TYPE_PARAM_NAME).toUpperCase()
          : props.preselectedAppointmentId,
        opticianId:
          urlParams.get(OPTICIAN_ID_PARAM_NAME) || props.preselectedOpticianId
      });
    }

    // If based in logic above or if props.openOnPageLoad explicitly passed as true from BE we open modal
    if (
      (shouldOpenFromQueryString || props.openOnPageLoad) &&
      !hasOpenedOnLoad.current
    ) {
      modalContext.toggleAppointmentBookingModal();
      hasOpenedOnLoad.current = true;
    }
  }, [props.openOnPageLoad, modalContext]);

  return (
    <Modal
      animate={false}
      className="appointment-booking-modal"
      containerClassName="appointment-booking-modal__inner"
      contentClassName="appointment-booking-modal__content"
      id={appBookId}
      isVisible={modalContext.isAppointmentBookingModalOpen}
      hide={modalContext.closeModals}
      hideCloseText={false}
    >
      {modalContext.isAppointmentBookingModalOpen && (
        <Collapse
          unmountChildren={true}
          isOpen={modalContext.isAppointmentBookingModalOpen}
        >
          <ModalContentWrapper>
            <AppointmentBooking
              getRegionsEndpoint={props.getRegionsEndpoint}
              getAppointmentsEndpoint={props.getAppointmentsEndpoint}
              getAvailabilityEndpoint={props.getAvailabilityEndpoint}
              googleMapsApiKey={props.googleMapsApiKey}
              {...props.bookingFormData}
              footerData={props.footerData}
              titles={props.titles}
              subtitles={props.subtitles}
              hints={props.hints}
              seeNearestText={props.seeNearestText}
              assistantText={props.assistantText}
              assistantDefaultText={props.assistantDefaultText}
              assistantCancelText={props.assistantCancelText}
              assistantLabel={props.assistantLabel}
              timeText={props.timeText}
              priceText={props.priceText}
              paymentTypeText={props.paymentTypeText}
              currentWeekDay={props.currentWeekDay}
              carouselTitle={props.carouselTitle}
              carouselPreviousItemText={props.carouselPreviousItemText}
              carouselNextItemText={props.carouselNextItemText}
              timeListTitle={props.timeListTitle}
              preposition={props.preposition}
              logo={props.logo}
              userAddress={props.userAddress}
              // Preselected values for AppointmentBooking component, either from props or from state query string if there are any
              preselectedShopId={preselectedValues.shopId}
              preselectedAppointmentId={preselectedValues.appointmentId}
              preselectedOpticianId={preselectedValues.opticianId}
            />
          </ModalContentWrapper>
        </Collapse>
      )}
    </Modal>
  );
};

AppointmentBookingModal.propTypes = {
  getRegionsEndpoint: PropTypes.string,
  getAppointmentsEndpoint: PropTypes.string,
  getAvailabilityEndpoint: PropTypes.string,
  googleMapsApiKey: PropTypes.string,
  bookingFormData: PropTypes.shape({
    stepBackButtonText: PropTypes.string,
    form: PropTypes.exact(Form.propTypes),
    conditionalTextAppointmentType: PropTypes.string,
    conditionalBookingUserNameText: PropTypes.string,
    appointmentBookingUserName: PropTypes.exact(TextInput.propTypes),
    appointmentBookingBirthDate: PropTypes.exact(TextInput.propTypes),
    appointmentBookingEmail: PropTypes.exact(TextInput.propTypes),
    appointmentBookingPhoneNumber: PropTypes.exact(TextInput.propTypes),
    appointmentBookingAttentionText: PropTypes.string,
    appointmentBookingAddInfo: PropTypes.exact(TextInput.propTypes),
    agreeBoxData: PropTypes.exact(AppointmentBookingAgreeBox.propTypes)
  }),
  footerData: PropTypes.exact(AppointmentBookingFooter.propTypes),
  titles: PropTypes.arrayOf(PropTypes.string),
  subtitles: PropTypes.arrayOf(PropTypes.string),
  hints: PropTypes.arrayOf(PropTypes.string),
  seeNearestText: PropTypes.string,
  assistantText: PropTypes.string,
  assistantDefaultText: PropTypes.string,
  assistantCancelText: PropTypes.string,
  assistantLabel: PropTypes.string,
  timeText: PropTypes.string,
  priceText: PropTypes.string,
  paymentTypeText: PropTypes.string,
  currentWeekDay: PropTypes.string,
  carouselTitle: PropTypes.string,
  carouselPreviousItemText: PropTypes.string,
  carouselNextItemText: PropTypes.string,
  timeListTitle: PropTypes.string,
  preposition: PropTypes.string,
  logo: PropTypes.string,
  preselectedShopId: PropTypes.string,
  preselectedAppointmentId: PropTypes.string,
  preselectedOpticianId: PropTypes.string,
  userAddress: PropTypes.shape({
    endpoint: PropTypes.string,
    address: PropTypes.string,
    latitude: PropTypes.string,
    longitude: PropTypes.string
  }),
  openOnPageLoad: PropTypes.bool,
  checkQueryString: PropTypes.bool
};

export default AppointmentBookingModal;
